import type { Map, StyleSpecification } from "maplibre-gl";

const APP_SOURCE_IDS = new Set(["imported-geojson-source", "image-overlay-source"]);
const APP_LAYER_ID_PREFIXES = ["imported-geojson-", "image-overlay-"];

/**
 * Keeps the MapLibre/OpenFreeMap basemap visually aligned with the original
 * Google Maps implementation.
 *
 * The original project applied Google map styles that disabled labels for:
 * administrative, landscape, POI, road, and transit layers. OpenFreeMap's
 * default Liberty style includes symbol layers for cities, countries, roads,
 * POIs, water labels, and transit labels. For this POC, those labels would
 * compete with the user's historical/GIS overlays, so they are hidden by
 * default.
 */
export class MapStyleService {
  static applyOriginalLabelFreeBasemap(map: Map): void {
    const style = map.getStyle() as StyleSpecification | undefined;
    const layers = style?.layers ?? [];

    for (const layer of layers) {
      if (this.isApplicationLayer(layer.id, "source" in layer ? layer.source : undefined)) continue;
      if (this.shouldHideBasemapLayer(layer)) {
        map.setLayoutProperty(layer.id, "visibility", "none");
      }
    }
  }

  private static shouldHideBasemapLayer(layer: StyleSpecification["layers"][number]): boolean {
    if (layer.type === "symbol") return true;

    const id = layer.id.toLowerCase();
    const sourceLayer = "source-layer" in layer && typeof layer["source-layer"] === "string" ? layer["source-layer"].toLowerCase() : "";

    // Extra safety for styles that keep labels in non-standard layer names.
    return [id, sourceLayer].some((value) =>
      /label|name|place|poi|road-number|housenumber|city|country|state|province|transport|transit/.test(value)
    );
  }

  private static isApplicationLayer(layerId: string, source: unknown): boolean {
    if (typeof source === "string" && APP_SOURCE_IDS.has(source)) return true;
    return APP_LAYER_ID_PREFIXES.some((prefix) => layerId.startsWith(prefix));
  }
}
